
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import VueTreeselect from "@riophae/vue-treeselect";
import { LOAD_CHILDREN_OPTIONS, ASYNC_SEARCH } from '@riophae/vue-treeselect'
import { ALL_WITH_INDETERMINATE, BRANCH_PRIORITY } from '@riophae/vue-treeselect/src/constants';
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import api from "@/services/api.service";
import { AreaNode, GroupAdvancedSearchQuery } from "@/types/sv-data/groups/AreaNode";
import { Getter, namespace } from "vuex-class";
import { TenantOverview } from "@/store/user-context/types";
import AdvancedAreaSearch from "@/components/AdvancedAreaSearch.vue";
import DrawdownBreadcrumbs from "@/components/area-tree-select/DrawdownBreadcrumbs.vue"
import AdditionalFields from "../area-tree-select/AdditionalFields.vue";
import organizationApi from "@/services/api.organizationSettings";
import { AreaSearchAdditionalFieldsSettingResponse } from "@/types/area-tree-select/AreaSearchAdditionalFieldsSettingResponse";
import { CustomFieldDto, CustomFieldTableTypes } from "@/store/custom-fields/types";
const UserContext = namespace("userContext");
const Areas = namespace("areas");
const CustomFields = namespace("customFields");

@Component({
	components: {
		VuePerfectScrollbar: VuePerfectScrollbar,
		"drawdown-breadcrumbs": DrawdownBreadcrumbs,
		"vue-treeselect": VueTreeselect,
		"advanced-area-search": AdvancedAreaSearch,
		"additional-fields": AdditionalFields
	}
})
export default class AreaTreeSelect extends Vue {
	$refs!: {
		fullComponent: any,
		treeSelect: VueTreeselect,
		breadcrumbComponent: DrawdownBreadcrumbs,
		additionalFields: AdditionalFields
	};

	@Prop() public value;
	@Prop({ default: () => { return ["Any"] } }) public requiredPermissions: string[];
	@Prop({ default: false }) public appendToBody: boolean;
	@Prop({ default: true }) public clearable: boolean;
	@Prop({ default: false }) public multiple: boolean;
	@Prop({ default: false }) public readonly: boolean;
	@Prop({ type: Function, default: option => option }) public reduce: (areaNode: AreaNode) => string;
	@Prop({ default: "Select an area" }) public placeholder: string;
	@Prop({ default: false }) public fullPathValue: boolean;
	@Prop({ default: false }) public showState: boolean;
	@Prop({ type: Boolean, default: true}) private useDrawdownSearch: boolean;
	@Prop({ type: Boolean, default: true}) private useAdvancedAreaSearch: boolean;
	@Prop({ type: Boolean, default: true}) private useAdditionalSearchFields: boolean;

	@UserContext.Getter getTenantSize: string;
	@UserContext.State("isSet") isUserContextSet: boolean;
	@UserContext.State tenantOverview: TenantOverview;
	@UserContext.Action("fetchOverview") fetchTenantOverview: () => Promise<void>;

	@Areas.Action fetchAreaDictionary: () => Promise<void>;
	@Areas.State areaDictionary: Map<number, string>;
	@CustomFields.Action retrieveCustomFields: ({ tableType, live }) => Promise<void>;
	@CustomFields.State areaCustomFields: CustomFieldDto[];

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	// The currently loaded AreaNodes (Note: these values are not what is shown when searching.)
	private areaTree: AreaNode[] = [];
	private isSearching: boolean = false;
	private isSearchComplete: boolean = true;

	// True if the user exists within a tenant that has over 500 groups.
	// Forces the user to search for an area before showing results.
	private isAsyncSearchEnabled: boolean = false;
	private isLoadingAreas: boolean = false;
	private fullPath = ALL_WITH_INDETERMINATE;
	private groupOnly = BRANCH_PRIORITY;
	private menuIsAlwaysOpen: boolean = false;

	// Drawdown Search variables
	// Flag to determine if the most recent action taken by the user was pinning a node
	private hasJustPinnedNode: boolean = false;

	// Advanced Search variables
	private isAdvancedSearchVisible: boolean = false;
	private isDeselectingInMultiple: boolean = false;
	private timeoutInterval: number = 100;
	private tabKeyIdentifier: string = "Tab";
	// Flag to determine if the results of the VueTreeselect are from an Advanced Search
	private isLastSearchAdvanced: boolean = false;
	// Stores the most recent query provided to the Advanced Search endpoint
	private lastAdvancedSearchQuery: GroupAdvancedSearchQuery = null;

	// Additional Search Fields variables
	private allSearchableAdditionalFields: AreaSearchAdditionalFieldsSettingResponse[] = [];

	// This is required due to VueTreeselect not re-rendering correctly
	// See https://github.com/riophae/vue-treeselect/issues/483
	private rerenderKey: number = 0;

	private async mounted(): Promise<void> {
		if (this.areaDictionary.size === 0) {
			await this.fetchAreaDictionary();
		}
		await this.loadAreaTree();
		this.applyAdditionalFunctionalityWatchers();
		if (this.isAdditionalSearchFieldsEnabled) {
			await this.fetchAdditionalSearchFields();
		}
	}

	private applyAdditionalFunctionalityWatchers(): void {
		if (this.isDrawdownSearchEnabled || this.isAdvancedSearchEnabled) {
			document.addEventListener("click", this.checkClickOutside);
			if (this.$refs.fullComponent) {
				this.$refs.fullComponent.addEventListener("keydown", this.checkTabKey);
			}
		}
		if (this.isAdvancedSearchEnabled || this.isAdditionalSearchFieldsEnabled) {
			this.addTreeSelectClickWatcher();
		}
	}

	private async fetchAdditionalSearchFields(): Promise<void> {
		let additionalFieldSettings = await organizationApi.retrieveAreaSearchAdditionalFieldsSetting(false);
		this.allSearchableAdditionalFields =
			Array.isArray(additionalFieldSettings) ?
				additionalFieldSettings :
				[] as AreaSearchAdditionalFieldsSettingResponse[];

		if (!(this.areaCustomFields.length > 0)) {
			await this.retrieveCustomFields({ tableType: CustomFieldTableTypes.Area, live: false});
		}
	}

	private get additionalFieldTitles(): string[] {
		return this.allSearchableAdditionalFields.map(sf => sf.title);
	}

	private beforeDestroy(): void {
		document.removeEventListener("click", this.checkClickOutside);
		if (this.$refs.fullComponent) {
			this.$refs.fullComponent.removeEventListener("keydown", this.checkTabKey);
		}
	}

	private get isDrawdownSearchEnabled(): boolean {
		return this.getFeature(["Areas", "SearchDrawdown"]) && this.useDrawdownSearch && !this.readonly;
	}

	private get isAdvancedSearchEnabled(): boolean {
		return this.getFeature(["Areas", "AdvancedSearch"]) && this.useAdvancedAreaSearch && !this.readonly;
	}

	private get isAdditionalSearchFieldsEnabled(): boolean {
		return this.getFeature(["Areas", "SearchAdditionalFields"]) && this.useAdditionalSearchFields && !this.readonly;
	}

	// Loads the area tree based on the number of groups in the current users tenant.
	@Watch("tenantOverview.areaCount")
	private async loadAreaTree(): Promise<void> {
		if (this.isUserContextSet) {
			this.isLoadingAreas = true;
			switch (this.getTenantSize) {
				case "small":
				case "medium":
					this.areaTree = this.setLeafNodes(await api.getFullGroupsTree(this.requiredPermissions));
					break;
				case "large":
					this.isAsyncSearchEnabled = true;
					break;
				default:
			}
			this.isLoadingAreas = false;
		} else {
			await this.fetchTenantOverview();
		}
	}

	public focus(): void {
		this.$refs.treeSelect?.getInput()?.focus();
	}

	@Watch("value")
	private async onValueChanged(): Promise<void> {
		if (!this.showState) {
			return;
		}

		// Incrementing the rerenderKey forces the vuetreeselect element to re-render
		this.rerenderKey += 1;
		await this.loadAreaTree();
	}

	private async loadOptions({ action, parentNode, callback, searchQuery }): Promise<void> {
		if (action === LOAD_CHILDREN_OPTIONS) {
			let groupTreeNode = await api.getGroupTreeNode(parentNode.id, this.requiredPermissions);
			parentNode.children = groupTreeNode.children;
			callback();
		}

		if (action === ASYNC_SEARCH) {
			// Set action variables to show/hide relevant information in the treeselect
			// Previous action was an async search, so we reset the flags corresponding to other search options
			this.isLastSearchAdvanced = false;
			this.hasJustPinnedNode = false;
			this.isSearching = true;
			this.isSearchComplete = false;

			let areaNodeSearchResult;

			areaNodeSearchResult = await this.searchWithExtraFeatures(searchQuery);

			if (!areaNodeSearchResult) {
				callback(null, []);
			}
			else {
				callback(null, [areaNodeSearchResult]);
			}

			this.isSearchComplete = true;
		}
	}

	private async searchWithExtraFeatures(searchQuery: string): Promise<AreaNode> {
		if (this.isAdditionalSearchFieldsEnabled) {
			this.isLastSearchAdvanced = false;
			if (this.additionalFieldTitles.length > 0) {
				if (this.isDrawdownSearchEnabled && this.$refs.breadcrumbComponent.breadcrumbs?.length > 1) {
					// Search with additional fields in subtree if pinned node exists
					return await api.searchUsingAdditionalFields(
						searchQuery,
						this.additionalFieldTitles,
						this.requiredPermissions,
						this.$refs.breadcrumbComponent.lowestLevelOfBreadcrumbs?.groupId)
				} else {
					// Otherwise, search with additional fields from root
					return await api.searchUsingAdditionalFields(
						searchQuery,
						this.additionalFieldTitles,
						this.requiredPermissions, null);
				}
			}
		}
		else if (this.isDrawdownSearchEnabled && this.$refs.breadcrumbComponent.breadcrumbs?.length > 1) {
			// Search below subtree if pinned node exists
			return await api.searchGroupSubtree(searchQuery, this.$refs.breadcrumbComponent.lowestLevelOfBreadcrumbs?.groupId, this.requiredPermissions);
		}

		return await api.searchGroupTree(searchQuery, this.requiredPermissions);
	}

	@Emit("is-open")
	private close(): boolean {
		this.isSearching = false;
		return false;
	}

	@Emit("is-open")
	private open(): boolean {
		if (this.isDrawdownSearchEnabled || this.isAdvancedSearchEnabled) {
			this.menuIsAlwaysOpen = true;
		}

		return true;
	}

	private async searchValueChanged(searchQuery): Promise<void> {
		this.isSearching = searchQuery.length > 0;
		if (!this.isAsyncSearchEnabled && this.isAdditionalSearchFieldsEnabled) {
			this.isLastSearchAdvanced = false;
			if (this.isDrawdownSearchEnabled && this.$refs.breadcrumbComponent.lowestLevelOfBreadcrumbs) {
				try {
					this.areaTree = [
					await api.searchUsingAdditionalFields(
						searchQuery,
						this.additionalFieldTitles,
						this.requiredPermissions,
						this.$refs.breadcrumbComponent.lowestLevelOfBreadcrumbs.groupId)];
				}
				catch {
					console.error("Error retrieving search results using Additional Fields");
					this.areaTree = [];
				}
				this.isSearchComplete = true;
				this.isSearching = false;
			}
		}
	}

	private onInputChanged(node) {
		if (!node || node.length === 0) {
			if(this.clearable){
				this.$emit("input", null);
			}
			return;
		}
		if (this.fullPathValue && node.length > 0) {
			let nodesToRemove: number [] = [];
			let selectedNode = node[0];
			let setNodesToRemove = (n) => {
				if (n.children && n.children.length > 0) {
					n.children.forEach(cn => {
						nodesToRemove.push(cn.id);
						setNodesToRemove(cn);
					});
				}
			}
			setNodesToRemove(selectedNode);
			let result = node.filter(n => !nodesToRemove.includes(n.id)).reverse();
			this.$emit("input", result);

			this.$refs.treeSelect.closeMenu();
			return;
		}
		if (this.isDrawdownSearchEnabled || this.isAdvancedSearchEnabled) {
			if (this.$refs.treeSelect && !this.$refs.treeSelect.multiple) {
				this.manuallyCloseTreeselectDropdown();
			}
		}
	}

	private selected(node) {
		if (this.fullPathValue === false) {
			if (this.multiple) {
				let result = [];
				if (this.value) {
					result = [...this.value];
				}
				result.push(this.reduce(node));
				this.$emit("input", result);
			} else {
				this.$emit("input", this.reduce(node));
			}
		}
	}

	private deselect(node) {
		if (this.multiple) {
			let result = [...this.value];
			let nodeToRemove = result.findIndex(n => n === node.id);
			if (nodeToRemove !== -1) {
				result.splice(nodeToRemove, 1);
			}
			this.isDeselectingInMultiple = true;

			this.$emit("input", result);
		} else {
			this.$emit("input", this.reduce(node));
		}
	}

	private setLeafNodes(areaNodes: AreaNode[]): AreaNode[] {
		if (!areaNodes)
			return;

		areaNodes.forEach(areaNode => {
			if (areaNode.children && areaNode.children.length > 0) {
				areaNode.children = this.setLeafNodes(areaNode.children);
			}
			else {
				areaNode.children = undefined;
			}
		});
		return areaNodes;
	}

	private get valueFormat(): string {
		if (this.fullPathValue) {
			return "node";
		}

		if (this.multiple) {
			return "id";
		}
		return typeof (this.value) === 'number' ? 'id' : 'node';
	}

	private incrementRerenderKey(focusElement: boolean = false): void {
		this.rerenderKey++;
		if (this.isAdvancedSearchEnabled || this.isAdditionalSearchFieldsEnabled) {
			setTimeout(() => {this.addTreeSelectClickWatcher(focusElement)}, this.timeoutInterval);
		}
	}

	// General navigation methods
	private checkClickOutside(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		if (!target.closest('.vue-treeselect__menu')) {
			const component = this.$refs.fullComponent as HTMLElement;
			if (component && !component.contains(event.target as Node)) {
				this.manuallyCloseTreeselectDropdown();
			}
		}
    }

	private manuallyCloseTreeselectDropdown(): void {
		this.menuIsAlwaysOpen = false;
		if (this.$refs.treeSelect) {
			this.$refs.treeSelect.closeMenu();
		}
	}

	// Methods for Drawdown Search
	private async buildPinnedTree(groupId: number): Promise<void> {
		this.hasJustPinnedNode = true;
		this.incrementRerenderKey();
		var children = await api.getGroupTreeNodeChildrenCount(groupId, this.requiredPermissions);
		if (children > 500) {
			this.isAsyncSearchEnabled = true;
			this.areaTree = [];
		}
		else {
			this.isAsyncSearchEnabled = false;
			this.isSearching = false;
			if (children == 0) {
				this.areaTree = [];
			} else {
				this.areaTree = this.setLeafNodes([await api.getGroupTreeNodeExtend(groupId, this.requiredPermissions)]);
			}
		}

		this.open();
	}

	private async pinArea(selectedNode: AreaNode): Promise<void> {
		await this.$refs.breadcrumbComponent.constructBreadcrumb(selectedNode.id, this.requiredPermissions);
		await this.buildPinnedTree(selectedNode.id);
	}

	// Methods for Advanced Search
	private showAdvancedSearch(): void {
		this.manuallyCloseTreeselectDropdown();
		this.isAdvancedSearchVisible = true;
	}

	private hideAdvancedSearch(): void {
		this.isAdvancedSearchVisible = false;
	}

	private async advancedSearch(searchQuery: GroupAdvancedSearchQuery): Promise<void> {
		this.lastAdvancedSearchQuery = searchQuery;
		this.isLastSearchAdvanced = true;
		this.hasJustPinnedNode = false;
		this.isAdvancedSearchVisible = false;
		try {
			let advancedSearchData = await api.advancedSearchGroupTree(searchQuery,
				this.requiredPermissions,
				this.isDrawdownSearchEnabled ?
				this.$refs.breadcrumbComponent.lowestLevelOfBreadcrumbs?.groupId :
				null);
			this.areaTree = advancedSearchData ? [advancedSearchData] : null;
			this.isAsyncSearchEnabled = false;
		}
		catch(ex) {
			console.error(ex)
		}
		this.incrementRerenderKey();
		this.open();
	}

	private get treeSelectId(): string {
		return "treeSelect-" + Math.random();
	}

	/*
		If the user generates results using the AdvancedSearch, the VueTreeselect will only apply any further searches to those results.
		Applying this click handlers means, if the user has previously generated result with the Advanced Search feature, a click into the
		text input will trigger an async search of the whole tenancy rather than just the results returned from the previous search
	*/
	private addTreeSelectClickWatcher(focusElement: boolean = false): void {
		var treeSelect = document.getElementById(this.treeSelectId);
		if (!treeSelect) {
			return;
		}

		// If using the multi-select, the input field has a different class
		if (this.treeselectMultiple) {
			var multipleContainer = treeSelect.getElementsByClassName("vue-treeselect__multi-value");
			if (!multipleContainer || multipleContainer.length <= 0) {
				return;
			}
			multipleContainer[0].addEventListener("click", (event) => { this.switchMode(event)});
		}

		// Add click handler to form input field
		var inputs = treeSelect.getElementsByClassName("vue-treeselect__input");
		if (!inputs || inputs.length <= 0) {
			return;
		}

		var input = inputs[0];
		input.addEventListener("click", (event) => { this.switchMode(event)});
		if (focusElement) {
			(input as HTMLElement).focus();
		}
	}

	private switchMode(event: Event): void {
		// Do not switch to async mode if deselecting a selected group if :multiple is true
		if (this.isDeselectingInMultiple) {
			event.stopPropagation();
			this.isDeselectingInMultiple = false;
			return;
		}

		// If user clicks on search input area, change to async mode to search beyond the results of the Advanced Search
		if (!this.isAsyncSearchEnabled) {
			this.isAsyncSearchEnabled = true;
		}
	}

	private checkTabKey(event: KeyboardEvent): void {
		if (event.key === this.tabKeyIdentifier) {
			// Close dropdown if tabbed away from input
			if(document.activeElement.classList.contains("vue-treeselect__input")){
				this.manuallyCloseTreeselectDropdown();
			}
		}
    }

	// To handle an existing bug, provide the option to use the VueTreeselect's :multiple value
	private get treeselectMultiple(): boolean {
		if (this.$refs.treeSelect) {
			return this.$refs.treeSelect.multiple
		} else {
			return false;
		}
	}

	private get showBeforeList(): boolean {
		// Show the 'Showing up to 500 results' when showing results from an API call which retrieves at most 500 results
		return (!this.isAsyncSearchEnabled && !this.hasJustPinnedNode)
			|| (this.isSearching && this.isSearchComplete);
	}

	private get shouldShowAdditionalFieldDisplay(): boolean {
		// Show the fields using which the user is searching when using the Advanced Search or Additional Search features
        return !this.hasJustPinnedNode &&
			((this.isAdditionalSearchFieldsEnabled && !this.isLastSearchAdvanced) ||
			(this.isAdvancedSearchEnabled && this.isLastSearchAdvanced));
    }
}
